import classnames from 'classnames';
import { capitalize, find, isNaN, orderBy } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import SemiCircularBar from '../../../../components/SemiCircularBar';
import {
  getCodesFromLC,
  getCustomerHighestLabel
} from '../../../../services/util';
import { fillColorForPredictionLevel } from '../crsDetailHeader';
import { NumberToMillionFormat } from '../riskDetailUtil';
import CLUSTER_INITIAL_DATA from './clusterInitialData';
import styles from './index.scss';

export const segmentMapping = [
  {
    segmentCode: 'B',
    segmentName: 'Corporate Banking'
  },
  {
    segmentCode: 'C',
    segmentName: 'Commercial Banking'
  },
  {
    segmentCode: 'E',
    segmentName: 'Private Banking'
  },
  {
    segmentCode: 'F',
    segmentName: 'Shareholders Fund'
  },
  {
    segmentCode: 'G',
    segmentName: 'Global Treasury'
  },
  {
    segmentCode: 'I',
    segmentName: 'Corporate Banking Overseas'
  },
  {
    segmentCode: 'J',
    segmentName: 'Priviledge Banking'
  },
  {
    segmentCode: 'K',
    segmentName: 'Financial Institutional Group'
  },
  {
    segmentCode: 'L',
    segmentName: 'Government Linked Companies (GLC)'
  },
  {
    segmentCode: 'M',
    segmentName: 'Business Banking'
  },
  {
    segmentCode: 'P',
    segmentName: 'PFS Mass'
  },
  {
    segmentCode: 'Q',
    segmentName: 'Multinational Corporates (MNS)'
  },
  {
    segmentCode: 'R',
    segmentName: 'Corporate Banking Regional'
  },
  {
    segmentCode: 'S',
    segmentName: 'Staff Loans'
  },
  {
    segmentCode: 'T',
    segmentName: 'Wealth Banking'
  },
  {
    segmentCode: 'V',
    segmentName: 'Priviledge Reserve'
  }
];

/**
 * CustomerAttributeRow component
 * @param clusterAttributes
 * @param isExpanded
 * @returns {*}
 * @constructor
 */
const CustomerAttributeRow = ({
  clusterDescriptionCodes,
  clusterInformation,
  isExpanded
}) => {
  if (clusterInformation) {
    console.log('clusterInformation', clusterInformation);
    const { clusterSegmentAttributes } = clusterInformation;
    let riskScoreLabel = '-';
    let riskScore = '-';
    let customerType = '-';
    let customerAge = '-';
    let customerTypeLabel = '-';
    let customerSegmentLabel = '-';
    let formattedCustomerSegment = '-';
    let customerAgeLabel = '-';
    let formattedCustomerType = '-';
    if (
      Array.isArray(clusterDescriptionCodes) &&
      clusterDescriptionCodes.length > 0
    ) {
      clusterDescriptionCodes.forEach((featureCode) => {
        clusterSegmentAttributes.forEach((clusterSegment) => {
          if (
            featureCode.code === 'PARTY_AGE' &&
            'PARTY_AGE' === clusterSegment.name
          ) {
            customerAgeLabel = 'Customer Age';
            customerAge = clusterSegment.feature_value;
          }
          if (
            featureCode.code === 'INDIVIDUAL_CORPORATE_TYPE' &&
            'INDIVIDUAL_CORPORATE_TYPE' === clusterSegment.name
          ) {
            customerTypeLabel = 'Customer Type (major)';
            customerType = clusterSegment.feature_value;
            formattedCustomerType = getCustomerHighestLabel(customerType);
          }
          if (
            featureCode.code === 'CUSTOMER_SEGMENT_CODE' &&
            'CUSTOMER_SEGMENT_CODE' === clusterSegment.name
          ) {
            customerSegmentLabel = 'Customer Segment (major)';
            formattedCustomerSegment = getCustomerHighestLabel(
              clusterSegment.feature_value
            );
            const { segmentName = 'NA' } = find(segmentMapping, {
              segmentCode: formattedCustomerSegment
            });
            formattedCustomerSegment = segmentName;
          }

          if (
            featureCode.code === 'RISK_SCORE' &&
            'RISK_SCORE' === clusterSegment.name
          ) {
            riskScoreLabel = 'Mean KYC Risk Score';
            riskScore = clusterSegment.feature_value;
          }
        });
      });
    }

    return (
      <div style={{ display: 'flex' }}>
        <div className={styles.containerTitle} style={{ width: 250 }}>
          Customer Attributes
        </div>
        <div style={{ width: '100%' }}>
          <div className={styles.flexEvenly}>
            {/* Customer Type */}
            <div
              style={{ width: 230, padding: 10 }}
              className={styles.flexEvenly}>
              <div style={{ marginRight: 10 }}>{customerTypeLabel}</div>
              <div style={{ fontWeight: 'bold', wordBreak: 'break-word' }}>
                {String(formattedCustomerType).toUpperCase().trim() === 'I'
                  ? 'Individual'
                  : String(formattedCustomerType).toUpperCase().trim() === 'C'
                  ? 'Corporate'
                  : formattedCustomerType}
              </div>
            </div>

            {/* Customer Segment */}
            <div
              style={{ width: 350, padding: 10 }}
              className={styles.flexEvenly}>
              <div style={{ marginRight: 10 }}>{customerSegmentLabel}</div>
              <div style={{ fontWeight: 'bold', wordBreak: 'break-word' }}>
                {formattedCustomerSegment}
              </div>
            </div>

            {/* Customer Age */}
            <div
              style={{ width: 200, padding: 10 }}
              className={styles.flexEvenly}>
              <div style={{ marginRight: 10 }}>{customerAgeLabel}</div>
              <div style={{ fontWeight: 'bold', wordBreak: 'break-word' }}>
                {isNaN(customerAge) || customerAge === '-'
                  ? '-'
                  : `${Number(customerAge).toFixed(0)} Yrs`}
              </div>
            </div>

            {/* Risk Score */}
            <div
              style={{ width: 200, padding: 10 }}
              className={styles.flexEvenly}>
              <div style={{ marginRight: 10 }}>{riskScoreLabel}</div>
              <div style={{ fontWeight: 'bold', wordBreak: 'break-word' }}>
                {isNaN(riskScore) || riskScore === '-'
                  ? '-'
                  : `${Number(riskScore).toFixed(2)}`}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

/**
 * Cluster Attributes component
 * @param clusterAttributes
 * @param isExpanded
 * @returns {*}
 * @constructor
 */
const ClusterAttributeRow = ({
  clusterDescriptionCodes,
  clusterInformation,
  isExpanded
}) => {
  if (clusterInformation) {
    const { clusterSegmentAttributes } = clusterInformation;
    let riskScoreLabel = '-';
    let riskScore = '-';
    if (
      Array.isArray(clusterDescriptionCodes) &&
      clusterDescriptionCodes.length > 0
    ) {
      clusterDescriptionCodes.forEach((featureCode) => {
        clusterSegmentAttributes.forEach((clusterSegment) => {
          if (
            featureCode.code === 'RISK_SCORE' &&
            'RISK_SCORE' === clusterSegment.name
          ) {
            riskScoreLabel = 'Mean KYC Risk Score';
            riskScore = clusterSegment.feature_value;
          }
        });
      });
    }

    return (
      <div style={{ display: 'flex' }}>
        <div className={styles.containerTitle} style={{ width: 250 }}>
          Cluster Attributes
        </div>
        <div style={{ width: '100%' }}>
          <div className={styles.flexEvenly}>
            {/* Risk Score */}
            <div
              style={{ width: 300, padding: 10 }}
              className={styles.flexEvenly}>
              <div style={{ marginRight: 10 }}>{riskScoreLabel}</div>
              {riskScore !== '-' && riskScore && (
                <SemiCircularBar
                  value={Number(riskScore).toFixed(2)}
                  color={fillColorForPredictionLevel(riskScore)}
                  thickness={10}
                  min={0}
                  max={100}
                  text="Score"
                  showMin
                  showMax
                  showText
                />
              )}
              {riskScore === '-' && <div style={{ fontWeight: 'bold' }}>-</div>}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

/**
 * HistoricalRiskRow component
 * @param historicalAttributes
 * @param clusterDescriptionCodes
 * @returns {*}
 * @constructor
 */
const HistoricalRiskRow = ({
  clusterDescriptionCodes,
  historicalAttributes
}) => {
  const { historicalAlertCount = '-', historicalStrCount = '-' } =
    historicalAttributes;
  let historicalAlertCountLabel = '-';
  let historicalStrCountLabel = '-';
  if (
    Array.isArray(clusterDescriptionCodes) &&
    clusterDescriptionCodes.length > 0
  ) {
    clusterDescriptionCodes.forEach((clusterCode) => {
      if (
        clusterCode?.code &&
        clusterCode.code === 'ALERT_90DAY_COUNT_CUSTOMER'
      ) {
        historicalAlertCountLabel = clusterCode.description;
      }

      if (
        clusterCode?.code &&
        clusterCode.code === 'STR_90DAY_COUNT_CUSTOMER'
      ) {
        historicalStrCountLabel = clusterCode.description;
      }
    });
  }
  return (
    <div style={{ display: 'flex' }}>
      <div className={styles.containerTitle} style={{ width: 250 }}>
        Historical Risk
      </div>
      <div style={{ width: '100%' }}>
        <div className={styles.flexEvenly}>
          <div
            style={{ padding: 10, marginRight: 20 }}
            className={styles.flexEvenly}>
            <div style={{ marginRight: 10 }}>{historicalAlertCountLabel}:</div>
            <div style={{ fontWeight: 'bold' }}>{historicalAlertCount}</div>
          </div>
          <div
            style={{ padding: 10, marginRight: 20 }}
            className={styles.flexEvenly}>
            <div style={{ marginRight: 10 }}>
              {historicalStrCountLabel
                ? historicalStrCountLabel
                : 'Total count of alerted customer with true alerts (STR) in last 90 days for the cluster.'}
            </div>
            <div style={{ fontWeight: 'bold' }}>{historicalStrCount}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * AttributeCell for Single cluster view attribute
 * @param idx
 * @param name
 * @param score
 * @param comparison
 * @returns {*}
 * @constructor
 */
const AttributeCell = (idx, name, score, comparison) => {
  let style = styles.featureBlue;
  switch (comparison) {
    case 'L':
      style = styles.featureBlue;
      break;
    case 'M':
      style = styles.featureYellow;
      break;
    case 'H':
      style = styles.featureRed;
      break;
  }

  return (
    <div className="col-sm-4">
      <div className={style}>
        <div
          className={styles.featureItem}
          style={{
            width: 320,
            display: 'flex',
            minHeight: 35,
            justifyContent: 'center',
            alignItems: 'center'
          }}>
          <div
            style={{
              width: 250,
              wordBreak: 'break-word'
            }}
            id={`cluster-attribute-${idx}`}>
            {capitalize(name)}
          </div>
          <div
            style={{
              padding: 0,
              width: 100,
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'center'
            }}>
            <b>{score}</b>
          </div>
        </div>
        {/* <ClusterToolTip className={styles.tooltipStyles} placement="bottom"
                          target={`cluster-attribute-${idx}`}>
            <div className={styles.toolTipItem}>{name}</div>
          </ClusterToolTip> */}
      </div>
    </div>
  );
};

/**
 * ClusterInformation component
 */
export class ClusterInformation extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isCollapsed: true,
      clusterDescCodes: []
    };
  }

  componentDidMount() {
    this.getClusterDescCodes();
  }

  onToggleCollapse = () => {
    const { isCollapsed } = this.state;
    this.setState({
      isCollapsed: !isCollapsed
    });
  };

  getClusterDescCodes = async () => {
    const clusterViewDescriptionCodes = await getCodesFromLC(
      'clusterViewDescription'
    );
    this.setState({ clusterDescCodes: clusterViewDescriptionCodes });
  };

  render() {
    const { latestCluster = {} } = this.props;
    const { isCollapsed, clusterDescCodes } = this.state;
    const { customerRiskScore = 'NA', clusterMeanScore = 'NA' } = latestCluster;
    const clusterDescriptionCodes =
      clusterDescCodes.length === 0 ? CLUSTER_INITIAL_DATA : clusterDescCodes;

    let riskScore = parseFloat(customerRiskScore);
    let meanScore = parseFloat(clusterMeanScore.split('%')[0]);
    let quadrantName = '';
    // Q1
    if (riskScore >= 50 && meanScore >= 50) {
      quadrantName = 'Q1';
    }
    // Q2
    else if (riskScore <= 50 && meanScore >= 50) {
      quadrantName = 'Q2';
    }

    // Q3
    else if (riskScore <= 50 && meanScore <= 50) {
      quadrantName = 'Q3';
    }

    // Q4
    else if (riskScore >= 50 && meanScore <= 50) {
      quadrantName = 'Q4';
    }

    if (this.props) {
      const { clusterInformation, isLoading } = this.props;
      const { clusterId = '-' } = clusterInformation;
      if (isLoading) {
        return (
          <div className={classnames('row', styles.clusterInformationWrapper)}>
            <div className="col-sm-12">
              <div className={styles.clusterInformationContainer}>
                <div className="row">
                  <div className="col-sm-2">
                    <span className={styles.containerTitle}>
                      Loading Cluster Information...
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      }

      if (
        clusterInformation &&
        Array.isArray(clusterInformation.clusterSegmentAttributes) &&
        clusterInformation.clusterSegmentAttributes.length > 0
      ) {
        const attributes = clusterInformation.clusterSegmentAttributes;
        const filteredAttributes = attributes.filter((attribute) => {
          const { name = '-' } = attribute || {};
          if (
            name === 'RISK_SCORE' ||
            name === 'PARTY_AGE' ||
            name === 'INDIVIDUAL_CORPORATE_TYPE' ||
            name === 'CUSTOMER_SEGMENT_CODE'
          ) {
            return false;
          }
          return !name.endsWith('_CLUSTER_SCORE');
        });
        const priority = { H: 1, M: 2, L: 3 };
        const attributesComparator = (attribute) =>
          priority[attribute.comparison];
        const orderedAttributes = isCollapsed
          ? orderBy(filteredAttributes, [attributesComparator], ['asc']).slice(
              0,
              3
            )
          : orderBy(filteredAttributes, [attributesComparator], ['asc']);
        const formattedAttributes = [];
        orderedAttributes.forEach((attribute, idx) => {
          let {
            feature_value = '-',
            name = '-',
            comparison = '-'
          } = attribute || {};
          if (feature_value !== '-') {
            if (String(name).toUpperCase().includes('_VOL_')) {
              feature_value = Number(feature_value);
            } else {
              feature_value = Number(feature_value).toFixed(2);
            }
          }
          const descriptionObject = find(clusterDescriptionCodes, {
            code: name
          });
          if (descriptionObject) {
            name = descriptionObject.description;
          }
          formattedAttributes.push(
            AttributeCell(
              idx,
              name,
              NumberToMillionFormat(feature_value),
              comparison
            )
          );
        });

        let collapseClass = isCollapsed ? 'fa fa-angle-down' : 'fa fa-angle-up';

        return (
          <div className={classnames('row', styles.clusterInformationWrapper)}>
            <div className="col-sm-12">
              <div className="row">
                <div className="col-sm-11">
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 500,
                      color: '#000000'
                    }}>
                    Cluster Features and Details [{' '}
                    {`Alert Yield - ${clusterMeanScore} , Risk Score - ${customerRiskScore}`}{' '}
                    ]
                  </div>
                </div>
                <div className="col-sm-1">
                  <div
                    onClick={this.onToggleCollapse}
                    className={styles.collapseButton}>
                    <i className={collapseClass}></i>
                  </div>
                </div>
              </div>

              <div className="col-sm-12" style={{ textAlign: 'right' }}>
                <div className={styles.clusterLegendsContainer}>
                  <div className={styles.legendItem}>
                    <span
                      className={classnames(styles.legendSymbol, styles.red)}>
                      &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                    </span>
                    &nbsp;
                    <span className="label-text">High</span>
                  </div>

                  <div className={styles.legendItem}>
                    <span
                      className={classnames(
                        styles.legendSymbol,
                        styles.yellow
                      )}>
                      &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                    </span>
                    &nbsp;
                    <span className="label-text">Medium</span>
                  </div>

                  <div className={styles.legendItem}>
                    <span
                      className={classnames(styles.legendSymbol, styles.blue)}>
                      &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                    </span>
                    &nbsp;
                    <span className="label-text">Low</span>
                  </div>
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  paddingTop: 10,
                  paddingBottom: 10
                }}>
                <div className={styles.containerTitle} style={{ width: 220 }}>
                  Cluster Code
                </div>
                <div
                  style={{
                    marginRight: 10,
                    fontWeight: 'bold'
                  }}>
                  {quadrantName +
                    '_' +
                    clusterMeanScore +
                    '_' +
                    customerRiskScore}
                </div>
              </div>

              {/* <ClusterAttributeRow
                    clusterDescriptionCodes={clusterDescriptionCodes}
                    isExpanded={false}
                    clusterInformation={clusterInformation}
                /> */}

              {/* <div style={{marginTop: '15px'}}></div> */}

              <CustomerAttributeRow
                clusterDescriptionCodes={clusterDescriptionCodes}
                isExpanded={false}
                clusterInformation={clusterInformation}
              />

              <div style={{ marginTop: '15px' }}></div>

              <HistoricalRiskRow
                clusterDescriptionCodes={clusterDescriptionCodes}
                historicalAttributes={clusterInformation}
              />

              <div style={{ marginTop: '15px' }}></div>

              <div className={styles.clusterInformationContainer}>
                <div className="row col-sm-12">
                  <div className={styles.containerTitle}>
                    Transaction Behaviour
                  </div>
                  <div className="row" style={{ marginLeft: 200 }}>
                    {formattedAttributes}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      }
    }

    return (
      <div className={classnames('row', styles.clusterInformationWrapper)}>
        <div className="col-sm-12">
          <div className={styles.clusterInformationContainer}>
            <div className="row">
              <div className="col-sm-2">
                <span className={styles.containerTitle}>
                  No data to display.
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

ClusterAttributeRow.propTypes = {
  clusterDescriptionCodes: PropTypes.array,
  clusterInformation: PropTypes.object,
  isExpanded: PropTypes.bool
};

CustomerAttributeRow.propTypes = {
  clusterDescriptionCodes: PropTypes.array,
  clusterInformation: PropTypes.object,
  isExpanded: PropTypes.bool
};

HistoricalRiskRow.propTypes = {
  clusterDescriptionCodes: PropTypes.array,
  historicalAttributes: PropTypes.object
};

/**
 * Proptypes
 * @type {{isLoading: shim, clusterInformation: shim}}
 */
ClusterInformation.propTypes = {
  latestCluster: PropTypes.object,
  clusterInformation: PropTypes.object,
  isLoading: PropTypes.bool
};
